import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

const CATEGORY_ORDER = ["Sans", "Serif", "Display", "Mono"];
let cdnLoaded = false;

export const loadFontPreviews = (fonts) => {
  if (cdnLoaded || !fonts?.length) return;
  cdnLoaded = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${fonts.map((f) => `family=${f.name.replace(/ /g, "+")}`).join("&")}&display=swap`;
  document.head.appendChild(link);
};

export const FontPicker = ({ label, value, onChange, fonts = [], required = false, placeholder, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (open) loadFontPreviews(fonts);
  }, [open, fonts]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const grouped = useMemo(() => {
    const filtered = fonts.filter((f) => f.name.toLowerCase().includes(q.trim().toLowerCase()));
    return CATEGORY_ORDER.map((c) => [c, filtered.filter((f) => f.category === c)]).filter(([, l]) => l.length);
  }, [fonts, q]);

  return (
    <div className="relative" ref={boxRef}>
      <p className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#9A9591] uppercase mb-1.5">
        {label} {required ? <span className="text-[#B4443C]">*</span> : <span className="normal-case tracking-normal">(optional)</span>}
      </p>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full sm:w-[240px] flex items-center justify-between gap-2 rounded-xl border border-[#EAE5DB] bg-white px-3.5 py-2.5 text-left hover:border-[#181621] transition-colors"
        data-testid={testid}
      >
        <span className="text-[14px] truncate" style={value ? { fontFamily: `'${value}', sans-serif` } : {}}>
          {value || <span className="text-[#9A9591] text-[13px]">{placeholder || "Pick a font…"}</span>}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && !required && (
            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onChange(""); }} className="text-[#9A9591] hover:text-[#181621]" data-testid={`${testid}-clear`}>
              <X size={13} />
            </span>
          )}
          <ChevronDown size={14} className="text-[#9A9591]" />
        </span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1.5 w-full sm:w-[300px] rounded-xl border border-[#EAE5DB] bg-white shadow-lg overflow-hidden" data-testid={`${testid}-menu`}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#F3EFE7]">
            <Search size={13} className="text-[#9A9591] shrink-0" />
            <input autoFocus className="w-full text-[13px] outline-none" placeholder="Search fonts…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
          </div>
          <div className="max-h-[300px] overflow-y-auto py-1">
            {grouped.length === 0 && <p className="px-3.5 py-3 text-[12.5px] text-[#9A9591]">No fonts match.</p>}
            {grouped.map(([cat, list]) => (
              <div key={cat}>
                <p className="font-mono-j text-[9px] tracking-[0.14em] text-[#9A9591] uppercase px-3.5 pt-2.5 pb-1">{cat}</p>
                {list.map((f) => (
                  <button
                    key={f.name}
                    type="button"
                    onClick={() => { onChange(f.name); setOpen(false); setQ(""); }}
                    className={`w-full text-left px-3.5 py-2 text-[15px] hover:bg-[#FAF8F3] ${value === f.name ? "bg-[#F3EFE7]" : ""}`}
                    style={{ fontFamily: `'${f.name}', sans-serif` }}
                    data-testid={`${testid}-option-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
